import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// Full history for one sales order: header/comments, stage history, each
// item's Smart Buyer fulfilment (which PO, when), and the blocked-order
// notes log -- everything needed to answer "what happened to this order and
// why" without manually cross-referencing five tables by hand.
// Usage: <OrderTraceModal orderRef={ref} onClose={() => setRef(null)} />

function fmtDateTime(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-NZ', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(`${val}T00:00:00`).toLocaleDateString('en-NZ', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

// Deliberately a plain hex, not var(--muted) -- that variable resolves to a
// dim #6B7280 elsewhere in the app that's hard to read against this modal's
// dark navy background. This is lighter/higher-contrast, scoped to this
// component only.
const MUTED = '#9AA9BC';

const stageColour = (stage) => {
  if (stage === 'Dispatched') return '#6FCF97';
  if (stage === 'Problem Encountered' || stage === 'To Call Customer') return '#C8922A';
  if (stage === 'HC - Back Order') return '#7DB3E8';
  return MUTED;
};

export default function OrderTraceModal({ orderRef, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase
      .rpc('get_order_trace', { p_order_ref: orderRef });
    if (rpcError) {
      setError(rpcError.message);
      setData(null);
    } else if (!result || !result.order) {
      setError(`No sales order found matching "${orderRef}"`);
      setData(null);
    } else {
      setData(result);
    }
    setLoading(false);
  }, [orderRef]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const order = data?.order;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(13, 21, 32, 0.55)', backdropFilter: 'blur(2px)', zIndex: 1000 }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 1001, width: 820, maxWidth: '95vw', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
        background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
        borderRadius: 10, boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        fontFamily: 'var(--font, Helvetica, sans-serif)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px 12px', borderBottom: '1px solid var(--border, #1E2D42)',
          background: 'var(--surface-raised, #0F1A28)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🔍</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text, #E8EDF4)' }}>
                Order Trace — {orderRef}
              </div>
              {order && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
                  {[order.first_name, order.last_name].filter(Boolean).join(' ') || '—'}
                  {order.email && ` · ${order.email}`}
                  {order.phone && ` · ${order.phone}`}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={load} disabled={loading} title="Refresh" style={{
              background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
              borderRadius: 5, color: MUTED, cursor: loading ? 'default' : 'pointer',
              fontSize: 13, padding: '4px 10px', opacity: loading ? 0.5 : 1,
            }}>
              {loading ? '…' : '↻'}
            </button>
            <button onClick={onClose} title="Close (Esc)" style={{
              background: 'transparent', border: 'none', color: MUTED,
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px',
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading…</div>
          )}
          {!loading && error && (
            <div style={{ padding: 24, color: '#FF6B6B', fontSize: 13 }}>❌ {error}</div>
          )}
          {!loading && !error && order && (
            <>
              {/* Order summary */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase' }}>Created</div>
                  <div style={{ fontSize: 13, color: 'var(--text, #E8EDF4)' }}>{fmtDateTime(order.created_date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase' }}>Current Stage</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: stageColour(order.stage) }}>{order.stage || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase' }}>Status</div>
                  <div style={{ fontSize: 13, color: 'var(--text, #E8EDF4)' }}>{order.status || '—'}</div>
                </div>
              </div>

              {/* Internal comments (Cin7) */}
              {order.internal_comments && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 4, textTransform: 'uppercase' }}>
                    Internal Comments (Cin7)
                  </div>
                  <div style={{
                    fontSize: 12, color: 'var(--text, #E8EDF4)', whiteSpace: 'pre-wrap',
                    background: 'var(--surface-raised, #0F1A28)', border: '1px solid var(--border, #1E2D42)',
                    borderRadius: 6, padding: '8px 10px',
                  }}>
                    {order.internal_comments}
                  </div>
                </div>
              )}

              {/* Stage history */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 4, textTransform: 'uppercase' }}>
                  Stage History
                </div>
                {!data.stage_history?.length && (
                  <div style={{ fontSize: 12, color: MUTED }}>No stage changes recorded.</div>
                )}
                {data.stage_history?.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '3px 0' }}>
                    <span style={{ color: MUTED, minWidth: 130 }}>{fmtDateTime(h.changed_at)}</span>
                    <span style={{ color: stageColour(h.stage), fontWeight: 600 }}>{h.stage}</span>
                  </div>
                ))}
              </div>

              {/* Items + fulfilment */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>
                  Items &amp; Smart Buyer Fulfilment
                </div>
                {!data.items?.length && (
                  <div style={{ fontSize: 12, color: MUTED }}>No line items found.</div>
                )}
                {data.items?.map((item, i) => (
                  <div key={i} style={{
                    border: '1px solid var(--border, #1E2D42)', borderRadius: 6,
                    padding: '8px 10px', marginBottom: 6,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                      <span>
                        <span className="code-badge code-badge--sm" style={{ marginRight: 6 }}>{item.item_code}</span>
                        <span style={{ fontSize: 12, color: 'var(--text, #E8EDF4)' }}>{item.item_name}</span>
                      </span>
                      <span style={{ fontSize: 11, color: MUTED }}>
                        ordered {item.qty_ordered} · moved {item.qty_moved ?? 0}
                      </span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {!item.fulfilment?.length && (
                        <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>
                          Never linked to a Smart Buyer order.
                        </div>
                      )}
                      {item.fulfilment?.map((f, j) => (
                        <div key={j} style={{ fontSize: 11, color: MUTED, display: 'flex', gap: 8 }}>
                          <span>{fmtDateTime(f.linked_at)}</span>
                          <span style={{ color: 'var(--accent, #C8922A)', fontFamily: 'monospace' }}>{f.po_number || '—'}</span>
                          <span>({f.link_status})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 6, textTransform: 'uppercase' }}>
                  Notes
                </div>
                {!data.notes?.length && (
                  <div style={{ fontSize: 12, color: MUTED }}>No notes logged for this order.</div>
                )}
                {data.notes?.map((n, i) => (
                  <div key={i} style={{ fontSize: 12, color: MUTED, padding: '4px 0' }}>
                    <span style={{ color: 'var(--text, #E8EDF4)' }}>{fmtDateTime(n.created_at)}</span>
                    {n.initials && <span style={{ color: 'var(--accent, #C8922A)', marginLeft: 6 }}>[{n.initials}]</span>}
                    <span style={{ marginLeft: 6 }}>{n.note}</span>
                    {n.follow_up_date && (
                      <span style={{ marginLeft: 6, fontSize: 11 }}>(follow up {fmtDate(n.follow_up_date)})</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px', borderTop: '1px solid var(--border, #1E2D42)',
          background: 'var(--surface-raised, #0F1A28)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: 'var(--surface, #151F2E)', border: '1px solid var(--border, #1E2D42)',
            borderRadius: 5, color: 'var(--text, #E8EDF4)', cursor: 'pointer', fontSize: 12, padding: '5px 14px',
          }}>Close</button>
        </div>
      </div>
    </>
  );
}
